import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Save,
  DollarSign,
  Calendar,
  User,
  Phone,
  CreditCard,
  UserCheck,
  Banknote,
  CalendarDays,
  Calculator
} from 'lucide-react';
import DateInput from '../common/DateInput';

const PaymentForm = ({ isOpen, onClose, onSave, payment = null, campaigns = [], travelers = [] }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    travelerId: '',
    campaignId: '',
    paymentType: 'cash',
    totalAmount: '',
    downPayment: '',
    monthlyInstallment: '',
    paymentDuration: '',
    paymentStatus: 'pending',
    nextPaymentDate: '',
    agentCommission: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [calculatedValues, setCalculatedValues] = useState({
    remainingBalance: 0,
    totalInstallmentAmount: 0
  });

  useEffect(() => {
    if (payment) {
      setFormData({
        travelerId: payment.travelerId || '',
        campaignId: payment.campaignId || '',
        paymentType: payment.paymentType || 'cash',
        totalAmount: payment.totalAmount?.toString() || '',
        downPayment: payment.downPayment?.toString() || '',
        monthlyInstallment: payment.monthlyInstallment?.toString() || '',
        paymentDuration: payment.paymentDuration?.toString() || '',
        paymentStatus: payment.paymentStatus || 'pending',
        nextPaymentDate: payment.nextPaymentDate || '',
        agentCommission: payment.agentCommission?.toString() || '',
        notes: payment.notes || ''
      });
    } else {
      setFormData({
        travelerId: '',
        campaignId: '',
        paymentType: 'cash',
        totalAmount: '',
        downPayment: '',
        monthlyInstallment: '',
        paymentDuration: '',
        paymentStatus: 'pending',
        nextPaymentDate: '',
        agentCommission: '',
        notes: ''
      });
    }
    setErrors({});
  }, [payment, isOpen]);

  useEffect(() => {
    if (formData.paymentType === 'installments') {
      calculateInstallmentValues();
    }
  }, [formData.totalAmount, formData.downPayment, formData.monthlyInstallment, formData.paymentDuration, formData.paymentType]);

  const calculateInstallmentValues = () => {
    const totalAmount = parseFloat(formData.totalAmount) || 0;
    const downPayment = parseFloat(formData.downPayment) || 0;
    const monthlyInstallment = parseFloat(formData.monthlyInstallment) || 0;
    const duration = parseInt(formData.paymentDuration) || 0;

    const totalInstallmentAmount = monthlyInstallment * duration;
    const remainingBalance = totalAmount - downPayment;

    setCalculatedValues({
      remainingBalance,
      totalInstallmentAmount
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Auto-calculate monthly installment if needed
    if (name === 'totalAmount' || name === 'downPayment' || name === 'paymentDuration') {
      if (formData.paymentType === 'installments') {
        const totalAmount = parseFloat(name === 'totalAmount' ? value : formData.totalAmount) || 0;
        const downPayment = parseFloat(name === 'downPayment' ? value : formData.downPayment) || 0;
        const duration = parseInt(name === 'paymentDuration' ? value : formData.paymentDuration) || 0;
        
        if (totalAmount > 0 && downPayment >= 0 && duration > 0) {
          const remainingAmount = totalAmount - downPayment;
          const monthlyInstallment = remainingAmount / duration;
          setFormData(prev => ({
            ...prev,
            monthlyInstallment: monthlyInstallment.toFixed(2)
          }));
        }
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.travelerId) {
      newErrors.travelerId = t('payments.validation.travelerRequired');
    }

    if (!formData.campaignId) {
      newErrors.campaignId = t('payments.validation.campaignRequired');
    }

    if (!formData.totalAmount || parseFloat(formData.totalAmount) <= 0) {
      newErrors.totalAmount = t('payments.validation.totalAmountRequired');
    }

    if (formData.paymentType === 'installments') {
      if (!formData.downPayment || parseFloat(formData.downPayment) < 0) {
        newErrors.downPayment = t('payments.validation.downPaymentRequired');
      }

      if (!formData.monthlyInstallment || parseFloat(formData.monthlyInstallment) <= 0) {
        newErrors.monthlyInstallment = t('payments.validation.monthlyInstallmentRequired');
      }

      if (!formData.paymentDuration || parseInt(formData.paymentDuration) <= 0) {
        newErrors.paymentDuration = t('payments.validation.paymentDurationRequired');
      }

      if (!formData.nextPaymentDate) {
        newErrors.nextPaymentDate = t('payments.validation.nextPaymentDateRequired');
      }

      // Validate that down payment doesn't exceed total amount
      const totalAmount = parseFloat(formData.totalAmount) || 0;
      const downPayment = parseFloat(formData.downPayment) || 0;
      if (downPayment > totalAmount) {
        newErrors.downPayment = t('payments.validation.downPaymentExceedsTotal');
      }
    }

    if (formData.agentCommission && parseFloat(formData.agentCommission) < 0) {
      newErrors.agentCommission = t('payments.validation.agentCommissionInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const paymentData = {
        ...formData,
        totalAmount: parseFloat(formData.totalAmount),
        downPayment: formData.paymentType === 'installments' ? parseFloat(formData.downPayment) : 0,
        monthlyInstallment: formData.paymentType === 'installments' ? parseFloat(formData.monthlyInstallment) : 0,
        paymentDuration: formData.paymentType === 'installments' ? parseInt(formData.paymentDuration) : 0,
        agentCommission: formData.agentCommission ? parseFloat(formData.agentCommission) : 0,
        remainingBalance: formData.paymentType === 'installments' ? calculatedValues.remainingBalance : 0
      };
      onSave(paymentData);
    }
  };

  const selectedTraveler = travelers.find(t => t.id === formData.travelerId);
  const selectedCampaign = campaigns.find(c => c.id === formData.campaignId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {payment ? t('payments.editPayment') : t('payments.addPayment')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Traveler and Campaign Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <User className="h-4 w-4 inline mr-1" />
                {t('payments.traveler')}
              </label>
              <select
                name="travelerId"
                value={formData.travelerId}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.travelerId ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('payments.selectTraveler')}</option>
                {travelers.map(traveler => (
                  <option key={traveler.id} value={traveler.id}>
                    {traveler.fullName} - {traveler.primaryPhone}
                  </option>
                ))}
              </select>
              {errors.travelerId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.travelerId}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                {t('payments.campaign')}
              </label>
              <select
                name="campaignId"
                value={formData.campaignId}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.campaignId ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('payments.selectCampaign')}</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name} - {campaign.destination}
                  </option>
                ))}
              </select>
              {errors.campaignId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.campaignId}</p>
              )}
            </div>
          </div>

          {/* Payment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('payments.paymentType')}
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="paymentType"
                  value="cash"
                  checked={formData.paymentType === 'cash'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <Banknote className="h-4 w-4 mr-1" />
                {t('payments.cash')}
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="paymentType"
                  value="installments"
                  checked={formData.paymentType === 'installments'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <CalendarDays className="h-4 w-4 mr-1" />
                {t('payments.installments')}
              </label>
            </div>
          </div>

          {/* Total Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <DollarSign className="h-4 w-4 inline mr-1" />
              {t('payments.totalAmount')}
            </label>
            <input
              type="number"
              name="totalAmount"
              value={formData.totalAmount}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              placeholder={t('payments.enterTotalAmount')}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                errors.totalAmount ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.totalAmount && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.totalAmount}</p>
            )}
          </div>

          {/* Installment Details */}
          {formData.paymentType === 'installments' && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-4">
              <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 flex items-center">
                <Calculator className="h-5 w-5 mr-2" />
                {t('payments.installmentDetails')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                    {t('payments.downPayment')}
                  </label>
                  <input
                    type="number"
                    name="downPayment"
                    value={formData.downPayment}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    placeholder={t('payments.enterDownPayment')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      errors.downPayment ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.downPayment && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.downPayment}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                    {t('payments.paymentDuration')} ({t('payments.months')})
                  </label>
                  <input
                    type="number"
                    name="paymentDuration"
                    value={formData.paymentDuration}
                    onChange={handleInputChange}
                    min="1"
                    placeholder={t('payments.enterPaymentDuration')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      errors.paymentDuration ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.paymentDuration && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.paymentDuration}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                    {t('payments.monthlyInstallment')}
                  </label>
                  <input
                    type="number"
                    name="monthlyInstallment"
                    value={formData.monthlyInstallment}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    placeholder={t('payments.enterMonthlyInstallment')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      errors.monthlyInstallment ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.monthlyInstallment && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.monthlyInstallment}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                    {t('payments.nextPaymentDue')}
                  </label>
                  <DateInput

 name="nextPaymentDate"
 value={formData.nextPaymentDate}
 onChange={handleInputChange}
 className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
 errors.nextPaymentDate ? 'border-red-500' : 'border-gray-300'
 }`}
 />
                  {errors.nextPaymentDate && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.nextPaymentDate}</p>
                  )}
                </div>
              </div>

              {/* Calculated Values */}
              {formData.totalAmount && formData.downPayment && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    {t('payments.calculatedValues')}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">
                        {t('payments.remainingBalance')}
                      </span>
                      <p className="font-medium text-gray-900 dark:text-white">
                        ${calculatedValues.remainingBalance.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">
                        {t('payments.totalInstallmentAmount')}
                      </span>
                      <p className="font-medium text-gray-900 dark:text-white">
                        ${calculatedValues.totalInstallmentAmount.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Payment Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('payments.paymentStatus')}
            </label>
            <select
              name="paymentStatus"
              value={formData.paymentStatus}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="pending">{t('payments.pending')}</option>
              <option value="partial">{t('payments.partial')}</option>
              <option value="paid">{t('payments.paid')}</option>
              <option value="overdue">{t('payments.overdue')}</option>
            </select>
          </div>

          {/* Agent Commission */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <UserCheck className="h-4 w-4 inline mr-1" />
              {t('payments.agentCommission')} ({t('common.optional')})
            </label>
            <input
              type="number"
              name="agentCommission"
              value={formData.agentCommission}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              placeholder={t('payments.enterAgentCommission')}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                errors.agentCommission ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.agentCommission && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.agentCommission}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('payments.notes')} ({t('common.optional')})
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder={t('payments.enterNotes')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save className="h-4 w-4 mr-2" />
              {payment ? t('common.update') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;